import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type {
  SystemControlResponse,
  SystemControlStateResponse,
  SystemStatus,
  GatewayConfigurationData,
  GatewayControlData,
  TradingRuntimeSettings,
} from './types/system.ts';

/** System controls, runtime settings, and Interactive Brokers gateway endpoints. */
export class SystemApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  getSystemStatus(): Promise<SystemStatus> {
    return this.request<SystemStatus>(`${this.baseUrl}/system/status`);
  }

  getSystemControlState(): Promise<SystemControlStateResponse> {
    return this.request<SystemControlStateResponse>(`${this.baseUrl}/system/control`, { timeoutMs: 15000 });
  }

  setSystemEnabled(enabled: boolean): Promise<SystemControlResponse> {
    return this.request<SystemControlResponse>(`${this.baseUrl}/system/enable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(enabled),
    });
  }

  setStrategyEnabled(strategyName: string, enabled: boolean): Promise<SystemControlResponse> {
    return this.request<SystemControlResponse>(`${this.baseUrl}/system/strategy/${encodeURIComponent(strategyName)}/enable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(enabled),
    });
  }

  emergencyLiquidate(): Promise<SystemControlResponse> {
    return this.request<SystemControlResponse>(`${this.baseUrl}/system/liquidate`, { method: 'POST' });
  }

  resetEmergencyMode(): Promise<SystemControlResponse> {
    return this.request<SystemControlResponse>(`${this.baseUrl}/system/emergency/reset`, { method: 'POST' });
  }

  connectIB(): Promise<SystemControlResponse> {
    return this.request<SystemControlResponse>(`${this.baseUrl}/system/ib/connect`, { method: 'POST' });
  }

  disconnectIB(): Promise<SystemControlResponse> {
    return this.request<SystemControlResponse>(`${this.baseUrl}/system/ib/disconnect`, { method: 'POST' });
  }

  controlIBGateway(
    action: 'start' | 'stop' | 'restart',
    gatewayId?: string,
  ): Promise<APIResponse<GatewayControlData>> {
    const params = new URLSearchParams();
    if (gatewayId) params.set('gateway_id', gatewayId);
    const query = params.toString();
    const endpoint = `${this.baseUrl}/system/ib/gateway/${action}${query ? `?${query}` : ''}`;
    return this.request<APIResponse<GatewayControlData>>(endpoint, { method: 'POST' });
  }

  getTradingRuntimeSettings(): Promise<APIResponse<TradingRuntimeSettings>> {
    return this.request<APIResponse<TradingRuntimeSettings>>(
      `${this.baseUrl}/admin/settings/trading`,
    );
  }

  updateTradingRuntimeSettings(
    settings: Partial<TradingRuntimeSettings>,
  ): Promise<APIResponse<TradingRuntimeSettings>> {
    return this.request<APIResponse<TradingRuntimeSettings>>(
      `${this.baseUrl}/admin/settings/trading`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
      },
    );
  }

  configureIBGateway(
    username: string,
    password: string,
    mode: 'paper' | 'live' = 'paper',
    gatewayId?: string,
    publicPort?: number,
    originalGatewayId?: string,
  ): Promise<APIResponse<GatewayConfigurationData>> {
    return this.request<APIResponse<GatewayConfigurationData>>(
      `${this.baseUrl}/system/ib/configure`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username,
        password,
        mode,
        gateway_id: gatewayId,
        public_port: publicPort,
        original_gateway_id: originalGatewayId,
      }),
      },
    );
  }

  deleteIBGatewayConfig(gatewayId: string): Promise<APIResponse<GatewayConfigurationData>> {
    const params = new URLSearchParams({ gateway_id: gatewayId });
    return this.request<APIResponse<GatewayConfigurationData>>(
      `${this.baseUrl}/system/ib/configure?${params.toString()}`,
      { method: 'DELETE' },
    );
  }

  activateIBGateway(gatewayId: string): Promise<APIResponse<GatewayConfigurationData>> {
    return this.request<APIResponse<GatewayConfigurationData>>(
      `${this.baseUrl}/system/ib/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gateway_id: gatewayId }),
      },
    );
  }
}
